import { useState, useEffect } from "react"
import { MantineProvider, Table, Button, Text, Group, Box, Card, Title, Pagination, Select, Modal, Badge, TextInput } from "@mantine/core"
import { FileText, ChevronUp, ChevronDown, Eye, Link, Search, X } from "lucide-react"
import { getAllUploadedFiles } from "../api/uploadedFileApi"
import { UploadedFileResponse } from "../models/responses/UploadedFileResponse"
import ProcessingStatusBadge from "../components/common/ProcessingStatusBadge"
import { convertDateTimeToLocalTime, displaySourceName } from "../infrastructures/utils"
import { useAuth } from "../customAuth/authStateHook"

type SortField = 'FileName' | 'DateUploadedUtc' | 'DateCompletedUtc' | 'Status'
type SortDirection = 'asc' | 'desc'

export default function UploadedFileGrid() {
    const [data, setData] = useState<UploadedFileResponse[]>([])
    const [currentPage, setCurrentPage] = useState(1)
    const [itemsPerPage, setItemsPerPage] = useState(10)
    const [sortField, setSortField] = useState<SortField | null>(null)
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
    const [previewFile, setPreviewFile] = useState<UploadedFileResponse | null>(null)
    const [isPreviewOpen, setIsPreviewOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState<string>('all')
    const { user, isSuperAdmin } = useAuth();

    useEffect(() => {
        console.log("user", user)
        if (user?.email) {
            const fetchUploadedFiles = async () => {
                const uploadedFiles = await getAllUploadedFiles()
                if (uploadedFiles?.length > 0) {
                    setData(uploadedFiles)
                }
            }
            fetchUploadedFiles()
        }
    }, [user])

    // Filter data
    const filteredData = data.filter((file) => {
        const matchesSearch = file.FileName.toLowerCase().includes(searchTerm.toLowerCase())
        const matchesStatus = statusFilter === 'all' || file.Status === parseInt(statusFilter)
        return matchesSearch && matchesStatus
    })

    // Sort data
    const sortedData = [...filteredData].sort((a, b) => {
        if (!sortField) return 0

        let aValue: any
        let bValue: any

        switch (sortField) {
            case 'FileName':
                aValue = a.FileName.toLowerCase()
                bValue = b.FileName.toLowerCase()
                break
            case 'DateUploadedUtc':
                aValue = new Date(a.DateUploadedUtc)
                bValue = new Date(b.DateUploadedUtc)
                break
            case 'DateCompletedUtc':
                aValue = a.DateCompletedUtc ? new Date(a.DateCompletedUtc) : new Date(0)
                bValue = b.DateCompletedUtc ? new Date(b.DateCompletedUtc) : new Date(0)
                break
            case 'Status':
                aValue = a.Status
                bValue = b.Status
                break
            default:
                return 0
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
        return 0
    })

    // Calculate pagination
    const totalPages = Math.ceil(sortedData.length / itemsPerPage)
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const currentData = sortedData.slice(startIndex, endIndex)

    // Handle edge case where current page exceeds available pages after filtering
    useEffect(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(totalPages)
        }
    }, [currentPage, totalPages])

    const handlePreview = (file: UploadedFileResponse) => {
        setPreviewFile(file)
        setIsPreviewOpen(true)
    }

    const handleClosePreview = () => {
        setIsPreviewOpen(false)
        setPreviewFile(null)
    }

    const handlePageChange = (page: number) => {
        setCurrentPage(page)
    }

    const handleItemsPerPageChange = (value: string | null) => {
        if (value) {
            setItemsPerPage(parseInt(value))
            setCurrentPage(1) // Reset to first page
        }
    }

    const handleSearchChange = (value: string) => {
        setSearchTerm(value)
        setCurrentPage(1) // Reset to first page when searching
    }

    const handleStatusFilterChange = (value: string | null) => {
        setStatusFilter(value || 'all')
        setCurrentPage(1) // Reset to first page when filtering
    }

    const clearFilters = () => {
        setSearchTerm('')
        setStatusFilter('all')
        setCurrentPage(1)
    }

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            // If clicking the same field, toggle direction
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
        } else {
            // If clicking a different field, set new field and default to asc
            setSortField(field)
            setSortDirection('asc')
        }
        setCurrentPage(1) // Reset to first page when sorting
    }

    const getSortIcon = (field: SortField) => {
        if (sortField !== field) return null
        return sortDirection === 'asc' ?
            <ChevronUp size={14} style={{ marginLeft: '4px' }} /> :
            <ChevronDown size={14} style={{ marginLeft: '4px' }} />
    }

    const tableStyles = {
        root: {
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1)",
        },
        thead: {
            backgroundColor: "#f8f9fa",
            borderBottom: "1px solid #e9ecef",
        },
    }

    const sortableHeaderStyle = {
        cursor: 'pointer',
        userSelect: 'none' as const,
        '&:hover': {
            backgroundColor: '#e9ecef',
        },
        transition: 'background-color 0.2s ease',
    }


    return (
        <MantineProvider>
            <div className="uploaded-files-container">
                <Box p="md" className="uploaded-files-inner-container">
                    <Card p="lg" radius="md" withBorder mb="md">
                        <Title order={4} mb="md">
                            Uploaded Files {data.length > 0 && `(${data.length} total)`}
                        </Title>

                        {/* Search and Filter Controls */}
                        {data.length > 0 && (
                            <Box mb="md">
                                <Group gap="md" mb="sm">
                                    <TextInput
                                        placeholder="Search files..."
                                        value={searchTerm}
                                        onChange={(e) => handleSearchChange(e.currentTarget.value)}
                                        leftSection={<Search size={16} />}
                                        style={{ flex: 1, minWidth: 200 }}
                                        rightSection={
                                            searchTerm && (
                                                <Button
                                                    variant="subtle"
                                                    size="xs"
                                                    p={0}
                                                    onClick={() => handleSearchChange('')}
                                                >
                                                    <X size={14} />
                                                </Button>
                                            )
                                        }
                                    />
                                    <Select
                                        placeholder="Filter by status"
                                        value={statusFilter}
                                        onChange={handleStatusFilterChange}
                                        data={[
                                            { value: 'all', label: 'All Statuses' },
                                            { value: '0', label: 'Pending' },
                                            { value: '1', label: 'Processing' },
                                            { value: '2', label: 'Completed' },
                                            { value: '3', label: 'Failed' },
                                        ]}
                                        style={{ minWidth: 150 }}
                                    />
                                    {(searchTerm || statusFilter !== 'all') && (
                                        <Button
                                            variant="light"
                                            size="xs"
                                            onClick={clearFilters}
                                        >
                                            Clear Filters
                                        </Button>
                                    )}
                                </Group>
                                <Text size="sm" c="dimmed">
                                    Showing {startIndex + 1}-{Math.min(endIndex, sortedData.length)} of {sortedData.length} files
                                    {filteredData.length !== data.length && ` (filtered from ${data.length} total)`}
                                </Text>
                            </Box>
                        )}

                        <Box style={{ borderRadius: "8px", overflow: "hidden" }}>
                            <Table striped highlightOnHover styles={tableStyles}>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th
                                            className="uploaded-file-name"
                                            style={sortableHeaderStyle}
                                            onClick={() => handleSort('FileName')}
                                        >
                                            <Group gap="xs">
                                                File Name
                                                {getSortIcon('FileName')}
                                            </Group>
                                        </Table.Th>
                                        <Table.Th>Source</Table.Th>
                                        <Table.Th>File</Table.Th>
                                        <Table.Th
                                            style={sortableHeaderStyle}
                                            onClick={() => handleSort('DateUploadedUtc')}
                                        >
                                            <Group gap="xs">
                                                Date Uploaded
                                                {getSortIcon('DateUploadedUtc')}
                                            </Group>
                                        </Table.Th>
                                        <Table.Th
                                            style={sortableHeaderStyle}
                                            onClick={() => handleSort('DateCompletedUtc')}
                                        >
                                            <Group gap="xs">
                                                Date Completed
                                                {getSortIcon('DateCompletedUtc')}
                                            </Group>
                                        </Table.Th>
                                        <Table.Th
                                            style={sortableHeaderStyle}
                                            onClick={() => handleSort('Status')}
                                        >
                                            <Group gap="xs">
                                                Status
                                                {getSortIcon('Status')}
                                            </Group>
                                        </Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {currentData.map((file) => (
                                        <Table.Tr
                                            key={file.Id}
                                            style={{
                                                transition: "background-color 0.2s ease",
                                                "&:hover": { backgroundColor: "#f8f9fa" },
                                            }}
                                        >
                                            <Table.Td className="uploaded-file-name">
                                                <Group>
                                                    <FileText size={16} style={{ marginRight: "8px" }} />
                                                    <div>
                                                        <Text size="sm" title={file.FileName}>
                                                            {file.FileName.length > 65 ? file.FileName.substring(0, 55) + "..." : file.FileName}
                                                        </Text>
                                                    </div>
                                                </Group>
                                            </Table.Td>
                                            <Table.Td>
                                                {displaySourceName(file.SourceName, file.SourceUrl)}
                                            </Table.Td>
                                            <Table.Td>
                                                <Group gap="xs">
                                                    <Button
                                                        variant="light"
                                                        color="green"
                                                        size="xs"
                                                        radius="md"
                                                        leftSection={<Eye size={12} />}
                                                        style={{
                                                            transition: "all 0.2s ease",
                                                            "&:hover": {
                                                                transform: "translateY(-2px)",
                                                                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                                                            },
                                                        }}
                                                        onClick={() => handlePreview(file)}
                                                    >
                                                        Preview
                                                    </Button>

                                                </Group>
                                            </Table.Td>

                                            <Table.Td>
                                                <Text size="sm">{convertDateTimeToLocalTime(file.DateUploadedUtc)}</Text>
                                            </Table.Td>
                                            <Table.Td>
                                                <Text size="sm">{convertDateTimeToLocalTime(file.DateCompletedUtc)}</Text>
                                            </Table.Td>
                                            <Table.Td>
                                                <ProcessingStatusBadge status={(file.Status)} />
                                            </Table.Td>
                                        </Table.Tr>
                                    ))}
                                </Table.Tbody>
                            </Table>
                        </Box>

                        {data.length > 0 && <Box mt="md" style={{ position: 'relative', }}>
                            {/* Items per page in the right corner */}
                            <Group gap="xs" style={{ position: 'absolute', right: 0, top: 0 }}>
                                <Text size="sm">Items per page:</Text>
                                <Select
                                    value={itemsPerPage.toString()}
                                    onChange={handleItemsPerPageChange}
                                    data={[
                                        { value: '10', label: '10' },
                                        { value: '25', label: '25' },
                                        { value: '50', label: '50' },
                                        { value: '100', label: '100' },
                                    ]}
                                    size="xs"
                                    w={70}
                                />
                            </Group>

                            {/* Pagination truly centered */}
                            {totalPages > 1 && (
                                <Group justify="center">
                                    <Pagination
                                        total={totalPages}
                                        value={currentPage}
                                        onChange={handlePageChange}
                                        size="sm"
                                        radius="md"
                                    />
                                </Group>
                            )}
                        </Box>}
                        {data.length === 0 && (
                            <Box mt="md" style={{ position: 'relative' }}>
                                <Text size="sm" c="dimmed">No files uploaded yet</Text>
                            </Box>
                        )}

                        {data.length > 0 && filteredData.length === 0 && (
                            <Box mt="md" style={{ position: 'relative' }}>
                                <Text size="sm" c="dimmed">No files match your search criteria</Text>
                            </Box>
                        )}
                    </Card>
                </Box>
            </div>

            {/* PDF Preview Modal */}
            <Modal
                opened={isPreviewOpen}
                onClose={handleClosePreview}
                title={previewFile ? `Preview: ${previewFile.FileName}` : 'Preview'}
                size="90%"
                radius="md"
                centered
                styles={{
                    content: {
                        height: '90vh',
                        maxHeight: '90vh',
                        overflow: 'hidden',
                    },
                    body: {
                        height: 'calc(90vh - 60px)',
                        padding: 0,
                    },
                }}
            >
                {previewFile && (
                    <Box style={{ height: '100%', position: 'relative' }}>
                        <iframe
                            src={`https://docs.google.com/viewer?url=${encodeURIComponent(previewFile.Url)}&embedded=true`}
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none',
                                borderRadius: '8px',
                            }}
                            title={`Preview of ${previewFile.FileName}`}
                            allow="fullscreen"
                        />
                    </Box>
                )}
            </Modal>
        </MantineProvider>
    )
}

